/*
 * Color Picker Modal Controller — used by the Calendar colors settings form.
 *
 * Wraps a single color row:
 *   - a hidden form field (`field` target) holds the current hex value
 *   - a Bootstrap modal (`modal` target) shows curated palette, Ambiki defaults,
 *     and a current/custom color picker
 *   - clicking the swatch trigger opens the modal
 *   - picking a color updates the field and closes the modal
 *
 * The native <input type="color"> fires `input` on every drag tick while its
 * picker is open, so that event only previews inside the modal; the hidden
 * field, the row swatch and the palette labels are written on `change`
 * (the committed value) or on an explicit pick/Apply. Curated palette labels
 * ("2 therapists", "Kurt Meyerhofer") are rebuilt from the sibling pickers'
 * fields each time a modal opens, so no cross-picker broadcast is needed.
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'field',
    'modal',
    'hexLabel',
    'preview',
    'nativeInput',
    'textInput',
    'applyButton',
    'currentSwatch',
    'currentHex',
    'swatch',
  ];

  static values = {
    label: String,
    // What this row currently shows, which is not always what the field submits: an
    // auto-assigned therapist has a blank field so saving can't record a computed color
    // as a deliberate pick. Read only as a fallback - once a pick is made the field wins.
    display: String,
    group: String,
  };

  open(event) {
    event.preventDefault();
    this.refreshSwatchLabels();
    this.highlightSelected();
    $(this.modalTarget).modal('show');
  }

  close() {
    $(this.modalTarget).modal('hide');
  }

  pick(event) {
    const { hex } = event.params;
    if (!hex) return;
    this.writeHex(hex);
    this.close();
  }

  // `input` from the native picker: fires continuously while dragging, so only
  // mirror the value into the modal's current/custom preview and hex box.
  previewFromNative(event) {
    const hex = event.target.value;
    if (!hex) return;
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = hex.toLowerCase();
      this.validateText();
    } else {
      this.previewInModal(hex.toLowerCase());
    }
  }

  // `change` from the native picker: the committed value. Write it through
  // like a swatch pick, but keep the modal open so the user can keep going.
  commitFromNative(event) {
    const hex = event.target.value;
    if (!hex) return;
    this.writeHex(hex);
  }

  validateText() {
    if (!this.hasTextInputTarget) return;
    const trimmed = this.textInputTarget.value.trim();
    const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
    const valid = match !== null;
    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = !valid;
    }
    // Live-preview the color as the user types
    if (valid) {
      this.previewInModal(`#${match[1].toLowerCase()}`);
    }
  }

  // Updates only the modal's current/custom preview; nothing outside the modal.
  previewInModal(hex) {
    if (this.hasCurrentSwatchTarget) {
      this.currentSwatchTarget.style.backgroundColor = hex;
    }
    if (this.hasCurrentHexTarget) {
      this.currentHexTarget.textContent = hex;
    }
    if (this.hasNativeInputTarget) {
      this.nativeInputTarget.value = hex;
    }
  }

  applyText() {
    if (!this.hasTextInputTarget) return;
    const trimmed = this.textInputTarget.value.trim();
    const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
    if (!match) return;
    const normalized = `#${match[1].toLowerCase()}`;
    this.writeHex(normalized);
    this.close();
  }

  submitOnEnter(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.applyText();
  }

  writeHex(rawHex) {
    const normalized = rawHex.toLowerCase();
    this.fieldTarget.value = normalized;
    if (this.hasHexLabelTarget) {
      this.hexLabelTarget.textContent = normalized;
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.style.backgroundColor = normalized;
    }
    this.previewInModal(normalized);
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = normalized;
    }
    this.highlightSelected();
    this.refreshSwatchLabels();
  }

  highlightSelected() {
    const current = (this.fieldTarget.value || this.displayValue).toLowerCase();
    this.swatchTargets.forEach((swatch) => {
      const hex = (swatch.dataset.colorPickerPopoverHexParam || '').toLowerCase();
      swatch.classList.toggle('cc-swatch-selected', hex === current);
    });
  }

  // Build a live color->label map from pickers in the same group, then update
  // this controller's curated palette swatches with labels.
  refreshSwatchLabels() {
    const colorCounts = {};
    const colorFirstLabel = {};
    const myGroup = this.groupValue;
    const groupNames = {
      'event-types': 'event types',
      therapists: 'therapists',
      locations: 'locations',
      disciplines: 'disciplines',
      'sub-types': 'sub-types',
    };
    const groupName = groupNames[myGroup] || 'items';

    document.querySelectorAll('[data-controller~="color-picker-popover"]').forEach((el) => {
      if (el.dataset.colorPickerPopoverGroupValue !== myGroup) return;
      const label = el.dataset.colorPickerPopoverLabelValue;
      const field = el.querySelector('[data-color-picker-popover-target="field"]');
      // Fall back to what the row displays when the field is blank. Field first, so a pick
      // made in this session counts immediately instead of the stale rendered value.
      const value = (field && field.value) || el.dataset.colorPickerPopoverDisplayValue;
      if (label && value) {
        const hex = value.toLowerCase();
        colorCounts[hex] = (colorCounts[hex] || 0) + 1;
        if (!colorFirstLabel[hex]) colorFirstLabel[hex] = label;
      }
    });

    this.swatchTargets.forEach((swatch) => {
      const hex = (swatch.dataset.colorPickerPopoverHexParam || '').toLowerCase();
      const count = colorCounts[hex] || 0;
      let labelSpan = swatch.querySelector('.cc-swatch-label');

      if (count > 0) {
        const text = count === 1 ? colorFirstLabel[hex] : `${count} ${groupName}`;
        swatch.classList.add('cc-swatch-labeled');
        if (!labelSpan) {
          labelSpan = document.createElement('span');
          labelSpan.className = 'cc-swatch-label';
          swatch.appendChild(labelSpan);
        }
        labelSpan.textContent = text;
      } else {
        swatch.classList.remove('cc-swatch-labeled');
        if (labelSpan) labelSpan.remove();
      }
    });
  }
}
